import axios from 'axios'
import router from '../router'
import Client from '@/commonFunc/client'
import BaseConfig from '@/config/base.js'
import SecretKey from '@/config/secret-key.js'

import { generateRandomString, getQueryParams } from '@/utils/common.js'
import { SignatureUtil } from '@/utils/sign.js'

// 不要toast
const noToastApi = [
  '/api/v2/app/withdraw/wallet/0/apply/check',
  '/api/v2/app/withdraw/wallet/1/apply/check',
  '/api/v2/app/shop/activity/27/cfg',
  '/api/v2/app/agent/invited/bind',
  '/api/v2/app/agent/invited/used',
]

// 按包短名注册的 API 域名解析策略
const apiBaseHostStrategies = {
  kiko(appPackageName, app) {
    const iosPackages = app.iosPackages || []
    return iosPackages.includes(appPackageName) ? app.apiBaseURL.ios : app.apiBaseURL.android
  },
}

function resolveApiBaseHost(appPackageName) {
  const shortName = BaseConfig.packageNameMap[appPackageName]
  const app = shortName ? BaseConfig.apps[shortName] : null
  const apiBaseURL = app?.apiBaseURL

  if (!apiBaseURL) {
    return BaseConfig.apps.vava?.apiBaseURL || ''
  }

  // 有策略走策略处理
  const strategy = apiBaseHostStrategies[shortName]
  if (strategy) {
    return strategy(appPackageName, app)
  }

  // 无策略则直接使用apiBaseURL
  return apiBaseURL
}

const isRelease = BaseConfig.isRelease
const language = Client.getAppInfoLang()

const { version, channel, deviceType, appPackageName, deviceId, timeZone } = Client.getAppInfo()

const apiHost = resolveApiBaseHost(appPackageName)
// 开发环境走 devServer（/api proxy + mock）；生产/测试构建直连 apiHost
const baseURL = process.env.NODE_ENV === 'development' ? '' : `https://${apiHost}`
const service = axios.create({ baseURL, timeout: 30000 })

// request拦截器
service.interceptors.request.use(
  (config) => {
    const { uid, token } = Client.getUserBaseInfo()

    const signObj = {
      timestamp: '',
      nonce: '',
      authorization: '',
    }

    // prettier-ignore
    try {
      let appSecret = appPackageName ? SecretKey.sg[appPackageName] : ''
      const apiPath = config.url.replace(/\?.*$/, '') || ''
      let businessParams = {}

      // 浏览器场景 没有appPackageName deviceId
      if (Client.env.isBrowser) {
        appSecret = isRelease ? SecretKey.sg['com.qumi.h5'] : SecretKey.sg['com.example.h5']
      }
      if (config.data) {
        businessParams = { ...config.data }
      }
      if (config.params) {
        businessParams = { ...config.params, ...businessParams }
      }
      const queryObj = getQueryParams(config.url)
      if (queryObj && Object.keys(queryObj).length) {
        businessParams = { ...queryObj, ...businessParams }
      }

      signObj.timestamp = Date.now()
      signObj.nonce = generateRandomString(8)
      signObj.authorization = SignatureUtil.genSignature(
        appPackageName,
        deviceId,
        apiPath,
        signObj.timestamp,
        signObj.nonce,
        appSecret,
        businessParams
      )
    }
    catch (err) {
      console.log('try catch', err)
    }

    const headers = {
      channel: channel || '',
      version: version || '',
      // 0 安卓 1 IOS
      deviceType: deviceType || 0,
      appPackageName: appPackageName || '',
      deviceId: deviceId || '',
      language: language || '',
      timeZone: timeZone || '',
      uid: uid || '',
      token: token || '',
      platform: 'h5',
      ...signObj,
    }

    // 本地调试代码
    if (window.location.hostname === 'localhost') {
      headers.version = '4.2.7'
      headers.deviceType = deviceType || 0
      headers.appPackageName = appPackageName || 'com.example.gcc'
      headers.deviceId = deviceId || '9b7cdeac-9ea6-4467-b9c8-d94bdc690d4d'
      headers.language = language || 'en'
      headers.timestamp = ''
      headers.nonce = ''
      headers.authorization = ''
    }

    config.headers = headers

    // prettier-ignore
    try {
      const method = (config.method || 'get').toUpperCase()
      const reqUrl = config.url || ''
      const tokenFlag = headers.token ? 1 : 0
      const signFlag = headers.authorization ? 1 : 0
      const reqMsg = `method=${method} url=${reqUrl} host=${apiHost} uid=${headers.uid} pkg=${headers.appPackageName} ver=${headers.version} deviceType=${headers.deviceType} deviceId=${headers.deviceId} lang=${headers.language} channel=${headers.channel} tz=${headers.timeZone} token=${tokenFlag} sign=${signFlag}`
      window.$postClientLog && window.$postClientLog(`[Req] ${reqMsg}`)
    }
    catch (err) {}

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// response拦截器
service.interceptors.response.use(
  (response) => {
    const res = response.data

    // prettier-ignore
    if (res.status !== 200) {
      // 异常toast (具体根据接口地址判断)
      const errorUrl = ((response || {}).config || {}).url || ''
      const description = ((res || {}).error || {}).description || ''
      if (!noToastApi.includes(errorUrl)) {
        Client.showToast(description || 'Network Error')
        if (!description) {
          // prettier-ignore
          try {
            const statusMsg = `Network Error url=${errorUrl} status=${(res || {}).status}`
            window.$postClientErr && window.$postClientErr('request.status', statusMsg)
          }
          catch (err) {}
        }
      }
    }

    return res
  },
  (error) => {
    // prettier-ignore
    try {
      const errorUrl = ((error || {}).config || {}).url || ''
      const httpStatus = ((error || {}).response || {}).status || ''
      const message = (error && error.message) || 'Network Error'
      const code = (error && error.code) || ''
      const networkMsg = `${message} url=${errorUrl} http=${httpStatus}${code ? ` code=${code}` : ''}`
      window.$postClientErr && window.$postClientErr('request.network', networkMsg)
    }
    catch (err) {}
    Client.showToast('Network Error')
    return Promise.reject(error)
  }
)

export default service
